import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SSpeedSelectorWrapper, SSpeedButton, SSpeedPopup, SSpeedOption } from './styled';

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

interface IProps {
  playbackRate: number;
  onChangeRate: (rate: number) => void;
}

const SpeedSelector: React.FC<IProps> = ({ playbackRate, onChangeRate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback(
    (rate: number) => {
      onChangeRate(rate);
      setIsOpen(false);
    },
    [onChangeRate]
  );

  // Close on click outside or Escape
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <SSpeedSelectorWrapper ref={wrapperRef}>
      <SSpeedButton
        onClick={toggleOpen}
        aria-label="Playback speed"
        aria-expanded={isOpen}
      >
        {playbackRate}x
      </SSpeedButton>

      {isOpen && (
        <SSpeedPopup role="listbox" aria-label="Select playback speed">
          {SPEED_OPTIONS.map((rate) => (
            <SSpeedOption
              key={rate}
              $active={rate === playbackRate}
              onClick={() => handleSelect(rate)}
              role="option"
              aria-selected={rate === playbackRate}
            >
              {rate}x
            </SSpeedOption>
          ))}
        </SSpeedPopup>
      )}
    </SSpeedSelectorWrapper>
  );
};

export default SpeedSelector;
